import { Tab, TabList, Tabs, Flex, Box, Button, useColorModeValue, HStack } from "@chakra-ui/react";
import VideoMarkupEditor from "./videoMarkupEditor";
import WisdomDocEditor from "./wisdomDocEditor";
import VideoInfoEditor from "./videoInfoEditor";
import MobileAppPreviewModal from "./mobileAppPreview";
import MarkupToolbar from "./markupToolbar";
import { MarkupTool } from "./videoMarkupEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faBookOpen, faEye, faMobileScreenButton } from "@fortawesome/free-solid-svg-icons";
import { useApp, useContentStore, useEditorSync, useMedia, useProblem, useSelectionReference, useUploadJobs, useS3Client } from "@/hooks/contextHooks";
import { ReactStateType } from "@xnav/types/src/frontend";
import { EditContent } from "@/pages/editor";
import { useEffect, useState, useContext } from "react";
import { AppMeta, Content, NewContentItem } from "@xnav/types/src/common";
import Loading from "./loading";
import { createClientContent, createServerContent } from "@/functions/editorFunctions";
import { apiGetRequest, apiPutRequest } from "@/functions/requests";
import { SelectedContentContext, NavCollapsedContext } from "@/contexts/contexts";
import { MediaMeta } from "@/pages/mediaManager";
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

export default function ContentEditor() {
	const app = useApp() as AppMeta;
	const [, setProblem] = useProblem();
	const [content, setContent] = useState<EditContent>();

	const [selectionReference] = useSelectionReference() as ReactStateType<NewContentItem | undefined>;
	const [pushJob, popJob] = useUploadJobs();

	const [contentStore, setContentStore] = useContentStore() as ReactStateType<Record<string, EditContent>>;
	const [editorSync] = useEditorSync();
	const [media] = useMedia() as ReactStateType<MediaMeta[]>;
	const s3 = useS3Client() as S3Client;
	const { user } = useAuthenticator();

	const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);

	async function fetchContentForItem(item: NewContentItem) {
		const temp: Content = {
			id: item.id,
			title: item.title,
			hotSpots: [],
			polygons: [],
			wisdomDocTitles: []
		};
		if ((item as any).videoId) temp.videoId = (item as any).videoId;
		if ((item as any).videoTitle) temp.videoTitle = (item as any).videoTitle;

		try {
			const command = new GetObjectCommand({
				Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
				Key: `${app.id}/contents/content/${item.id}.json`
			});
			const url = await getSignedUrl(s3 as any, command as any, { expiresIn: 360 });
			const response = await fetch(url);
			if (response.ok) {
				const strData = await response.text();
				let parsed = JSON.parse(strData);
				if (typeof parsed === "string") parsed = JSON.parse(parsed);
				const client = createClientContent(parsed);
				if (client) {
					if (!client.id) client.id = item.id;
					if (!client.title) client.title = item.title;
					if ((item as any).videoId && !client.videoId) client.videoId = (item as any).videoId;
					if ((item as any).videoTitle && !client.videoTitle) client.videoTitle = (item as any).videoTitle;
					if (client.videoTitle && !client.videoId) {
						client.videoId = media?.find((m: any) => m.name === client.videoTitle)?.id;
					}
					if (!client.videoTitle && !client.videoId) {
						const matchedMedia = media?.find(
							(m: any) => m.references?.includes(item.id) || m.name === item.title || m.id === item.id
						);
						if (matchedMedia) {
							client.videoId = matchedMedia.id;
							client.videoTitle = matchedMedia.name;
						}
					}
					return client;
				}
			}
		} catch (e) {
			console.log("S3 direct fetch failed, trying API", e);
		}

		try {
			let result = await apiGetRequest<string, Content | string>(`/content/${app.id}/${item.id}`).catch();
			if (typeof result === "string" && result !== "") result = JSON.parse(result);
			if (result && typeof result === "object") {
				const client = createClientContent(result as Content);
				if (client) {
					if (!client.id) client.id = item.id;
					if (!client.title) client.title = item.title;
					if ((item as any).videoId && !client.videoId) client.videoId = (item as any).videoId;
					if ((item as any).videoTitle && !client.videoTitle) client.videoTitle = (item as any).videoTitle;
					if (client.videoTitle && !client.videoId) {
						client.videoId = media?.find((m: any) => m.name === client.videoTitle)?.id;
					}
					return client;
				}
			}
		} catch (e) {
			console.log("API fetch failed, fallback to temp", e);
		}

		const matchedFallback = media?.find(
			(m: any) => m.references?.includes(item.id) || m.name === item.title || m.id === item.id
		);
		if (matchedFallback) {
			temp.videoId = matchedFallback.id;
			temp.videoTitle = matchedFallback.name;
		}
		return createClientContent(temp);
	}

	useEffect(() => {
		if (!selectionReference || selectionReference.type !== "video") {
			setContent(undefined);
			return;
		}

		if (
			contentStore[selectionReference.id] &&
			(contentStore[selectionReference.id].videoTitle || contentStore[selectionReference.id].videoId)
		) {
			setContent(contentStore[selectionReference.id]);
			return;
		}

		let isMounted = true;
		setContent(undefined);
		async function load() {
			const fetched = await fetchContentForItem(selectionReference!);
			if (isMounted) {
				setContent(fetched);
				if (fetched && fetched.id && fetched.id !== 'undefined') {
					setContentStore((prev) => ({ ...prev, [fetched.id]: fetched }));
				}
			}
		}
		load();

		return () => {
			isMounted = false;
		};
	}, [selectionReference?.id]);

	useEffect(() => {
		if (content && content.id && content.id !== 'undefined') {
			setContentStore((prev) => {
				if (prev[content.id] === content) return prev;
				return { ...prev, [content.id]: content };
			});
		}
	}, [content, setContentStore]);

	const [tabIndex, setTabIndex] = useState(0);
	const [tool, setTool] = useState<MarkupTool>("pointer");

	const [isNavCollapsed] = useContext(NavCollapsedContext);

	if (!selectionReference || selectionReference.type !== "video") return <></>;
	if (!content) return <Loading message="Fetching content" />;

	return (
		<SelectedContentContext.Provider value={[content, setContent]}>
			<Flex flexDir="column" flex={1} minH={0} w="full">
				<Flex
					w="full"
					align="center"
					justify="space-between"
					py={1}
					px={3}
					minH="38px"
					borderBottom="1px solid"
					borderColor={useColorModeValue("gray.200", "gray.750")}
					bg={useColorModeValue("white", "gray.900")}
				>
					<Box minH="32px" display="flex" alignItems="center">
						{tabIndex === 0 && (
							<MarkupToolbar tool={tool} setTool={setTool} orientation="horizontal" />
						)}
					</Box>
					<HStack spacing={3} ml="auto">
						<Tabs index={tabIndex} onChange={setTabIndex} variant="unstyled">
							<TabList
								bg={useColorModeValue("gray.100", "gray.800")}
								p={0.5}
								borderRadius="full"
								w="fit-content"
								boxShadow="inner"
							>
								<Tab
									borderRadius="full"
									px={4}
									py={1}
									fontWeight="600"
									fontSize="xs"
									color={useColorModeValue("gray.600", "gray.400")}
									_selected={{
										bg: useColorModeValue("white", "gray.700"),
										shadow: "md",
										color: useColorModeValue("blue.600", "blue.300")
									}}
									_hover={{ color: useColorModeValue("blue.500", "blue.200") }}
									transition="all 0.2s"
								>
									<FontAwesomeIcon icon={faVideo} style={{ marginRight: "6px" }} />
									Video Markup
								</Tab>
								<Tab
									borderRadius="full"
									px={4}
									py={1}
									fontWeight="600"
									fontSize="xs"
									color={useColorModeValue("gray.600", "gray.400")}
									_selected={{
										bg: useColorModeValue("white", "gray.700"),
										shadow: "md",
										color: useColorModeValue("blue.600", "blue.300")
									}}
									_hover={{ color: useColorModeValue("blue.500", "blue.200") }}
									transition="all 0.2s"
								>
									<FontAwesomeIcon icon={faEye} style={{ marginRight: "6px" }} />
									Info Overlay
								</Tab>
							</TabList>
						</Tabs>

						{/* Dedicated Mobile Preview Pop-up Trigger */}
						<Button
							size="xs"
							variant="solid"
							colorScheme="green"
							borderRadius="full"
							px={3.5}
							py={1.5}
							h="28px"
							fontSize="xs"
							fontWeight="700"
							leftIcon={<FontAwesomeIcon icon={faMobileScreenButton} />}
							onClick={() => setIsMobilePreviewOpen(true)}
							boxShadow="0 2px 8px rgba(16, 185, 129, 0.3)"
							_hover={{ transform: "scale(1.05)", bg: "emerald.500" }}
							transition="all 0.2s"
						>
							Mobile Preview
						</Button>
					</HStack>
				</Flex>

				<Flex flex={1} minH={0} w="full" pos="relative">
					{tabIndex === 0 && <VideoMarkupEditor tool={tool} setTool={setTool} />}
					{tabIndex === 1 && <VideoInfoEditor content={content} setContent={setContent as any} />}
				</Flex>

				{/* Smooth Popup Modal for Mobile Preview */}
				<MobileAppPreviewModal
					isOpen={isMobilePreviewOpen}
					onClose={() => setIsMobilePreviewOpen(false)}
					content={content}
				/>
			</Flex>
		</SelectedContentContext.Provider>
	);
}
